import {Component, computed, DestroyRef, inject, signal, WritableSignal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {dinamicQuestionModules} from '../../../../data/questionData';
import {ResultsService} from '../../../../core/service/results/results.service';
import {BackButton} from '../../../../../assets/shared/back-button/back-button';
import {CourseTests, Question} from '../../../../core/interface/questions.interface';
import {IsTest, ModuleType, TestingResult} from '../interface/testing.interface';
import {RadioButton} from 'primeng/radiobutton';
import {TestsService} from '../../../../core/service/tests/tests.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ConfirmationModal} from '../../../../../assets/shared/modal/confirmation-modal/confirmation-modal';
import {NotificationService} from '../../../../../assets/shared/notification/notification.service';
import {switchMap} from 'rxjs/operators';
import {EMPTY} from 'rxjs';

@Component({
  selector: 'testing-page',
  imports: [
    ReactiveFormsModule,
    BackButton,
    RadioButton,
    ConfirmationModal
  ],
  templateUrl: './testing-page.html',
  styleUrl: './testing-page.scss',
})
export class TestingPage {

  private notification = inject(NotificationService);
  private destroy = inject(DestroyRef);
  private resultService = inject(ResultsService)
  private testsService = inject(TestsService)
  private moduleData:CourseTests[] = dinamicQuestionModules
  private coursesId:WritableSignal<string | null> =signal(null)
  private moduleTest:WritableSignal<string | null  | undefined> = signal(null);
  private moduleType:WritableSignal<ModuleType> = signal('');
  private result:WritableSignal<TestingResult | undefined>= signal(undefined);
  public testingForm!: FormGroup;
  public questionNumber:WritableSignal<number> = signal(0);
  public dinamicData = signal<Question[]>([]);
  public isModal:WritableSignal<boolean> = signal(false);
  public isFinished:WritableSignal<boolean> = signal(this.questionNumber() === this.dinamicData()!.length - 1)
  public correctAnswersCount:WritableSignal<number| undefined> = signal(undefined);
  public isLastQuestion = computed(() => {
    return this.questionNumber() === this.dinamicData()!.length - 1;
  });


  constructor(private router:Router,private activeRouter:ActivatedRoute) {
    this.moduleType.set(this.activeRouter.snapshot.paramMap.get('type') as ModuleType);
    this.moduleTest.set(this.activeRouter.snapshot.paramMap.get('module'));
    this.coursesId.set(this.activeRouter.snapshot.parent?.paramMap.get('coursesId')!);
  }

  ngOnInit(){
    this.initModule();
  }

  private initModule(): void {
    if (!this.moduleType() || !this.moduleTest() || !this.coursesId()) {
      this.router.navigate(['**']);
      return;
    }

    const course = this.moduleData.find(item => String(item.courseId) === String(this.coursesId()));
    if (!course) {
      this.router.navigate(['**']);
      return;
    }

    const foundItem = course.modules.find(item => item.moduleType === this.moduleType()  && item.moduleNumber === this.moduleTest());
    if(foundItem?.moduleType === 'final'){
      this.correctAnswersCount?.set(foundItem?.correctAnswersCount)
    }

    if (!foundItem) {
      this.router.navigate(['**']);
      return;
    }

    this.dinamicData.set(foundItem.questions!);
    this.createForm();
  }

  private createForm() {
    const controls: Record<string, FormControl> = {};

    this.dinamicData()?.forEach(group => {
      const controlName = group.options[0].controlName;
      controls[controlName] = new FormControl(
        null,
        Validators.required
      );
    });
    this.testingForm = new FormGroup(controls);
  }


  get currentControlName(): string {
    return this.dinamicData()![this.questionNumber()]?.options[0].controlName;
  }


  public nextQuestion() {
    const questions = this.dinamicData();
    const currentIndex = this.questionNumber();
    const currentQuestion = questions[currentIndex];

    if (!currentQuestion) return;

    const controlName: string = currentQuestion.options[0].controlName;
    const control = this.testingForm.get(controlName);

    if (control?.invalid) {
      control.markAsTouched();
      return;
    }
    this.result.set(this.testingForm.value);

    if (currentIndex + 1 !== questions.length) {
      this.questionNumber.update(v => v + 1);
    }

    if (currentIndex === 0 && this.moduleType() === 'final') {
      this.testsService
        .isStartTest()
        .pipe(
          takeUntilDestroyed(this.destroy),
          switchMap((data: IsTest | IsTest[]) => {

            if (Array.isArray(data) && data.length === 0) {
              return this.testsService.startTest(this.coursesId()!);
            }
            
            if (!Array.isArray(data)) {
              if (data.courseId === this.coursesId() && data.startedAt === null) {
                return this.testsService.startTest(this.coursesId()!);
              }
            }

            return EMPTY;
          })
        )
        .subscribe({
          next: () => {},
          error: (err) => {
            this.notification.show("error", `${err?.error?.detail}`, '');
          }
        });
    }
  }

  public checkResult(){
    if(this.isLastQuestion()){
      this.result.set(this.testingForm.value);
    }

    if (!this.result()) return;
    let correctCount = 0;

    this.dinamicData()?.forEach(question => {
      const name:string = question.options[0].controlName;
      const userAnswer = this.result()![name];

      if (userAnswer === question.correct) {
        correctCount++;
      }
    });

    this.result.set({
      ...this.result(),
      answer: correctCount,
      allQuestion: this.dinamicData().length,
      correctAnswersCount: this.correctAnswersCount(),
      module: this.moduleTest(),
      type: this.moduleType()
    });

    this.resultService.results = this.result()

    this.router.navigate([this.coursesId(), 'test', 'result']);
    this.testingForm.reset()
}

  public navigateTesting() {
    return `${this.coursesId()}/testing/${this.moduleType()}/${this.moduleTest()}`;
  }

  public setModal(){
    document.body.classList.add('no-scroll');
    this.isModal.set(true)
  }

  public skipButton(){
    document.body.classList.remove('no-scroll');
    this.router.navigate([this.coursesId()])
  }

  public toTest(){
    this.isModal.set(false)
    document.body.classList.remove('no-scroll');
  }

  public prevQuestion(){
    if(this.questionNumber() === 0 ) return
    this.questionNumber.update(value => value - 1);

  }

}
